import { Container, type ContainerOptions } from "../core/Container.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import type { Point, Rect, Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawTriangle } from "../themes/glyphs.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { ListBox } from "./ListBox.js";
import { TextBox } from "./TextBox.js";

export interface ComboBoxOptions<T = string> extends ContainerOptions {
  items?: readonly T[];
  selectedIndex?: number;
  /** Text for an item; defaults to String(item). */
  itemText?: (item: T, index: number) => string;
  /** Let the user type a value as well as pick one (WinForms' DropDown). */
  editable?: boolean;
  /** Shown when nothing is selected. */
  placeholder?: string;
  font?: FontSpec;
  /** Rows visible before the drop-down starts scrolling. */
  maxDropDownItems?: number;
  onSelectionChanged?: () => void;
}

/** How long a run of keystrokes counts as one type-ahead search. */
const TYPE_AHEAD_MS = 800;

/**
 * A drop-down list, optionally with an editable text field.
 *
 * The list is a real ListBox shown through the desktop's popup layer, so it
 * escapes the owning window's bounds the way a native combo does: a combo near
 * the bottom of a short dialog still drops a full-height list. Dismissal comes
 * free with that, since a press anywhere outside a popup closes it.
 */
export class ComboBox<T = string> extends Container {
  #items: readonly T[];
  #selectedIndex = -1;
  #editable: boolean;
  #placeholder: string;
  #font: FontSpec | null;
  #textBox: TextBox | null = null;
  #list: ListBox<T> | null = null;
  /** Selection to restore if the drop-down is canceled with Escape. */
  #indexBeforeOpen = -1;
  #typeAhead = "";
  #typeAheadAt = 0;

  itemText: (item: T, index: number) => string;
  maxDropDownItems: number;

  constructor(options: ComboBoxOptions<T> = {}) {
    super({ width: 180, height: 30, tabStop: true, cursor: "default", ...options });
    this.#items = options.items ?? [];
    this.#editable = options.editable ?? false;
    this.#placeholder = options.placeholder ?? "";
    this.#font = options.font ?? null;
    this.itemText = options.itemText ?? ((item): string => String(item));
    this.maxDropDownItems = options.maxDropDownItems ?? 8;

    if (this.#editable) this.#createTextBox();
    if (options.selectedIndex !== undefined) this.selectedIndex = options.selectedIndex;
    if (options.onSelectionChanged) this.on("selectionchanged", options.onSelectionChanged);

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  // --- model ------------------------------------------------------------

  get items(): readonly T[] {
    return this.#items;
  }
  set items(v: readonly T[]) {
    this.#items = v;
    if (this.#selectedIndex >= v.length) this.selectedIndex = v.length - 1;
    if (this.#list) this.#list.items = v;
    this.#syncText();
    this.invalidate();
  }

  get count(): number {
    return this.#items.length;
  }

  get selectedIndex(): number {
    return this.#selectedIndex;
  }
  set selectedIndex(v: number) {
    const next = v < 0 || v >= this.#items.length ? -1 : v;
    if (next === this.#selectedIndex) return;
    this.#selectedIndex = next;
    this.#syncText();
    this.invalidate();
    this.emit("selectionchanged");
  }

  get selectedItem(): T | undefined {
    return this.#selectedIndex < 0 ? undefined : this.#items[this.#selectedIndex];
  }
  set selectedItem(v: T | undefined) {
    this.selectedIndex = v === undefined ? -1 : this.#items.indexOf(v);
  }

  /** The editable field's text, or the selected item's text when not editable. */
  get text(): string {
    if (this.#textBox) return this.#textBox.text;
    const item = this.selectedItem;
    return item === undefined ? "" : this.itemText(item, this.#selectedIndex);
  }
  set text(v: string) {
    if (this.#textBox) this.#textBox.text = v;
  }

  get editable(): boolean {
    return this.#editable;
  }

  /** The editable variant's caret lives in its text box, so that is where focus goes. */
  override focus(): boolean {
    return this.#textBox ? this.#textBox.focus() : super.focus();
  }

  /** Select all of the editable field's text. */
  selectAll(): void {
    this.#textBox?.selectAll();
  }

  get placeholder(): string {
    return this.#placeholder;
  }
  set placeholder(v: string) {
    this.#placeholder = v;
    if (this.#textBox) this.#textBox.placeholder = v;
    this.invalidate();
  }

  // --- drop-down --------------------------------------------------------

  get droppedDown(): boolean {
    return this.#list !== null && this.desktop?.popup === this.#list;
  }

  /** Width of the arrow well at the right-hand end of the field. */
  get arrowWidth(): number {
    const theme = this.desktop?.theme;
    return Math.max(16, Math.round((theme?.metrics.scrollBarSize ?? 14) + 6));
  }

  get arrowRect(): Rect {
    const w = Math.min(this.arrowWidth, this.width);
    return { x: this.width - w, y: 0, width: w, height: this.height };
  }

  /** The part of the field the text occupies, left of the arrow. */
  get fieldRect(): Rect {
    return { x: 0, y: 0, width: Math.max(0, this.width - this.arrowWidth), height: this.height };
  }

  toggleDropDown(): void {
    if (this.droppedDown) this.closeDropDown();
    else this.openDropDown();
  }

  openDropDown(): void {
    const desktop = this.desktop;
    if (!desktop || this.droppedDown || !this.effectivelyEnabled || this.#items.length === 0) return;

    const m = desktop.theme.metrics;
    const rows = Math.min(this.maxDropDownItems, this.#items.length);
    const anchor = this.screenBounds;

    const list = new ListBox<T>({
      x: anchor.x,
      y: anchor.y + anchor.height,
      width: anchor.width,
      height: rows * m.listItemHeight + m.listPadding.top + m.listPadding.bottom,
      items: this.#items,
      selectedIndex: this.#selectedIndex,
      itemText: this.itemText,
      horizontalScroll: "never",
      ...(this.#font ? { font: this.#font } : {}),
    });

    // A press commits; arrowing inside the list only moves the highlight.
    list.on("click", () => this.#commit(list.selectedIndex));
    list.on("itemactivate", (i) => this.#commit(i));
    // Focus leaving the list closes it. This has to watch the list rather than
    // the combo: opening focuses the list, which blurs the combo, so a blur
    // handler there would slam the drop-down shut the instant it appeared.
    list.on("blur", () => this.closeDropDown());
    list.on("keydown", (e) => {
      if (e.key === "Enter") {
        this.#commit(list.selectedIndex);
        e.handled = true;
      } else if (e.key === "Escape") {
        this.#cancel();
        e.handled = true;
      }
    });

    this.#list = list;
    this.#indexBeforeOpen = this.#selectedIndex;
    desktop.showPopup(list);

    // Borders and padding differ per theme, so measure the chrome from the
    // attached list rather than guessing, then size it to hold `rows` exactly.
    // Otherwise Classic's 3D bevel eats a row and shows a needless scroll bar.
    const chrome = Math.max(0, list.height - list.viewportRect.height);
    list.height = rows * m.listItemHeight + chrome;

    // Drop upward when there is not enough room below, as a native combo does.
    const below = anchor.y + anchor.height;
    list.y = below + list.height <= desktop.height ? below : Math.max(0, anchor.y - list.height);

    list.focus();
    if (this.#selectedIndex >= 0) list.ensureVisible(this.#selectedIndex);
    this.invalidate();
  }

  closeDropDown(): void {
    if (!this.#list) return;
    const desktop = this.desktop;
    const wasOpen = desktop?.popup === this.#list;
    this.#list = null;
    if (wasOpen) desktop?.closePopup();
    this.invalidate();
  }

  #commit(index: number): void {
    this.closeDropDown();
    this.selectedIndex = index;
    this.focus();
  }

  #cancel(): void {
    const restore = this.#indexBeforeOpen;
    this.closeDropDown();
    this.selectedIndex = restore;
    this.focus();
  }

  // --- editable field ---------------------------------------------------

  #createTextBox(): void {
    const box = new TextBox({
      x: 0,
      y: 0,
      width: 10,
      height: 10,
      placeholder: this.#placeholder,
      // The combo already draws the frame around the whole control.
      frame: false,
      ...(this.#font ? { font: this.#font } : {}),
    });
    box.on("textchanged", () => this.emit("textchanged"));
    this.#textBox = box;
    this.controls.add(box);
    // Nothing has resized yet, so place it now. Attaching to a desktop later
    // re-runs this with the real theme metrics behind arrowWidth.
    this.performLayout();
  }

  #syncText(): void {
    if (!this.#textBox) return;
    const item = this.selectedItem;
    this.#textBox.text = item === undefined ? "" : this.itemText(item, this.#selectedIndex);
  }

  protected override performLayout(): void {
    if (!this.#textBox) return;
    const field = this.fieldRect;
    this.#textBox.setBounds(field.x, field.y, field.width, field.height);
  }

  // --- input ------------------------------------------------------------

  protected override hitTestSelf(_p: Point): boolean {
    return true;
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (!this.effectivelyEnabled) return;
    // An editable combo only drops down from the arrow, leaving the rest of
    // the field to the text box. A list-only combo drops from anywhere.
    if (this.#editable && e.x < this.arrowRect.x) return;
    this.focus();
    this.toggleDropDown();
    e.handled = true;
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (!this.effectivelyEnabled) return;

    if (e.key === "F4" || (e.altKey && (e.key === "ArrowDown" || e.key === "ArrowUp"))) {
      this.toggleDropDown();
      e.handled = true;
      return;
    }
    // While the list is open it owns the keyboard.
    if (this.droppedDown || e.ctrlKey || e.metaKey || e.altKey) return;

    switch (e.key) {
      case "ArrowDown":
        this.#step(1);
        e.handled = true;
        return;
      case "ArrowUp":
        this.#step(-1);
        e.handled = true;
        return;
      case "Home":
        if (this.#items.length > 0) this.selectedIndex = 0;
        e.handled = true;
        return;
      case "End":
        if (this.#items.length > 0) this.selectedIndex = this.#items.length - 1;
        e.handled = true;
        return;
      default:
        break;
    }

    // Type-ahead; the editable variant types into its field instead.
    if (!this.#editable && e.key.length === 1 && this.#typeAheadFind(e.key)) {
      e.handled = true;
    }
  }

  #step(delta: number): void {
    if (this.#items.length === 0) return;
    const from =
      this.#selectedIndex < 0 ? (delta > 0 ? -1 : this.#items.length) : this.#selectedIndex;
    this.selectedIndex = Math.max(0, Math.min(this.#items.length - 1, from + delta));
  }

  /** Jump to the next item starting with the accumulated prefix. */
  #typeAheadFind(char: string): boolean {
    const now = typeof performance === "undefined" ? 0 : performance.now();
    if (now - this.#typeAheadAt > TYPE_AHEAD_MS) this.#typeAhead = "";
    this.#typeAheadAt = now;

    // Repeating a single letter cycles through the items beginning with it.
    const cycling = this.#typeAhead === char;
    this.#typeAhead = cycling ? char : this.#typeAhead + char;
    const prefix = this.#typeAhead.toLowerCase();

    const n = this.#items.length;
    if (n === 0) return false;
    const start = this.#typeAhead.length === 1 ? this.#selectedIndex + 1 : 0;
    for (let i = 0; i < n; i++) {
      const index = ((start + i) % n + n) % n;
      if (this.itemText(this.#items[index]!, index).toLowerCase().startsWith(prefix)) {
        this.selectedIndex = index;
        return true;
      }
    }
    return false;
  }

  // --- painting ---------------------------------------------------------


  /**
   * One theme control-height tall; the width stays whatever it was given.
   *
   * A field has no content width worth measuring — it is as wide as the form wants it, not as
   * wide as the text someone happens to have typed — so the only interesting axis is the one the
   * theme decides.
   */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    return { width: this.width, height: desktop.theme.metrics.controlHeight };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const base = this.visualState();
    const state: VisualState = {
      ...base,
      // The field reads as focused while its list is open, and while the inner
      // text box has focus in the editable variant.
      focused: base.focused || this.droppedDown || (this.#textBox?.focused ?? false),
    };
    theme.drawTextBox(rc, this.localRect, state);

    const arrow = this.arrowRect;
    // Themes that put stepper buttons on their scroll bars want a real button
    // here too; flat themes want a bare glyph.
    if (theme.metrics.scrollBarArrows) {
      theme.drawButton(
        rc,
        { x: arrow.x, y: 2, width: arrow.width - 2, height: arrow.height - 4 },
        { hovered: false, pressed: this.droppedDown, focused: false, disabled: state.disabled },
      );
    }

    const ink = state.disabled
      ? theme.palette.textDisabled
      : this.droppedDown
        ? theme.palette.accent
        : theme.palette.text;
    drawTriangle(rc, arrow, "down", ink, 4);

    if (!this.#editable) {
      const font = this.#font ?? theme.metrics.font;
      const pad = theme.metrics.textBoxPadding;
      const item = this.selectedItem;
      const label =
        item === undefined ? this.#placeholder : this.itemText(item, this.#selectedIndex);
      if (label) {
        rc.drawTextInRect(
          label,
          {
            x: pad.left,
            y: 0,
            width: Math.max(0, this.width - this.arrowWidth - pad.left - 2),
            height: this.height,
          },
          font,
          state.disabled
            ? theme.palette.textDisabled
            : item === undefined
              ? theme.palette.placeholderText
              : theme.palette.fieldText,
        );
      }
      if (state.focused && !state.disabled) theme.drawFocusRing(rc, this.localRect);
    }
  }
}
